// Live total indicator for percentage-bucket field groups.
//
// Used by both the operator and recipient forms to surface the running
// sum of a percentage group (mode-of-transaction, delivery-time mix)
// as the user types. CardConnect rejects payloads where these buckets
// don't sum to 100, which historically produced an opaque 400 — the
// inline indicator turns that into instant feedback so the user sees
// the error before they hit Submit.
//
// aria-live="polite" so screen readers announce the running total
// without interrupting the user mid-input. role="status" pairs with
// polite to match the WAI-ARIA pattern for transient status updates.

import { CheckCircle2, CircleDashed } from "lucide-react";

interface Props {
  /** Sum of the percentages in the group. */
  total: number;
  /** Optional id for the descriptor — lets fields aria-describedby this. */
  id?: string;
  /** Override the target. Defaults to 100. */
  target?: number;
}

export function PercentageTotalIndicator({ total, id, target = 100 }: Props) {
  const ok = total === target;
  // Amber for off-target; emerald for spot-on. Both have sufficient
  // contrast on the dark brand background per WCAG AA.
  const tone = ok
    ? "border-success/30 bg-success/10 text-success"
    : "border-warning/30 bg-warning/10 text-warning";
  const Icon = ok ? CheckCircle2 : CircleDashed;
  return (
    <p
      id={id}
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${tone}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      <span>
        Total {total}%{ok ? "" : ` — must be ${target}%`}
      </span>
    </p>
  );
}
